// src/pages/admin/tabs/collections/CollectionEditPage.jsx
import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import PropertyImagesPicker from '../../components/PropertyImagesPicker';
import MediaPickerModal from '../../components/MediaPickerModal';
import { normalizeMediaUrl } from '../../../../api/base';

const Wrap = styled.div`display:grid; gap:20px;`;
const Card = styled.div`
  border:1px solid var(--bdr); background:var(--card); border-radius:14px; padding:16px; box-shadow:var(--shadow);
`;
const Grid2 = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:12px;
  @media (max-width: 980px){ grid-template-columns:1fr; }
`;
const Field = styled.label`
  display:grid; gap:6px;
  input, textarea, select { width:100%; }
  textarea{ min-height: 110px; }
`;
const Actions = styled.div`display:flex; gap:10px; flex-wrap:wrap; align-items:center;`;
const Btn = styled.button`
  padding:10px 14px; border-radius:12px; border:1px solid var(--bdr);
  background:var(--card); font-weight:700; cursor:pointer;
  &[data-primary="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
  &[disabled]{ opacity:.6; cursor:default; }
`;
const Pills = styled.div`display:flex; gap:8px; flex-wrap:wrap; align-items:center;`;
const Pill = styled.span`
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 10px; border:1px solid var(--bdr); border-radius:999px; font-size:13px; opacity:.85;
`;
const Hr = styled.hr`border:none; border-top:1px dashed var(--bdr); margin:12px 0;`;

/* ---- DnD стили ---- */
const ItemsGrid = styled.div`display:grid; gap:8px;`;
const Row = styled.div`
  display:grid; grid-template-columns: 60px 1fr 110px 100px 100px; gap:8px;
  align-items:center; padding:8px; border:1px solid var(--bdr); border-radius:10px; background:var(--card);
  opacity:${({$dragging}) => $dragging ? .55 : 1};
  @media (max-width: 900px){ grid-template-columns: 60px 1fr 100px 90px; }
`;
const Thumb = styled.img`width:60px; height:44px; object-fit:cover; border-radius:6px;`;
const DragHandle = styled.span`cursor:grab; opacity:.7;`;

/* ---- Поиск объектов ---- */
const SearchBar = styled.div`display:flex; gap:8px; flex-wrap:wrap; align-items:center;`;
const Table = styled.table`
  width:100%; border-collapse:collapse; margin-top:10px;
  th, td{ border-bottom:1px solid var(--bdr); padding:8px; text-align:left; font-size:14px; }
  img{ width:64px; height:44px; object-fit:cover; border-radius:6px; }
`;

function ToggleYesNo({ value, onChange }){
  const v = !!value;
  return (
    <div style={{display:'inline-flex', border:'1px solid var(--bdr)', borderRadius:10, overflow:'hidden'}}>
      <button type="button" onClick={()=>onChange(true)}  style={{padding:'8px 12px', fontWeight:700, border:'none', background: v ? 'var(--primary)' : 'var(--card)', color: v ? 'var(--primaryFg)' : 'inherit'}}>Да</button>
      <button type="button" onClick={()=>onChange(false)} style={{padding:'8px 12px', fontWeight:700, border:'none', background: !v ? 'var(--primary)' : 'var(--card)', color: !v ? 'var(--primaryFg)' : 'inherit'}}>Нет</button>
    </div>
  );
}

function getThumb(p){
  const raw = p?.cover_thumb || p?.cover_url || p?.images?.find?.(x=>x?.thumbnail_url)?.thumbnail_url || p?.images?.[0]?.url || '';
  return normalizeMediaUrl(raw);
}

export default function CollectionEditPage(){
  const { id } = useParams(); // undefined => create NEW
  const isEdit = !!id;
  const nav = useNavigate();

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // коллекция
  const [slug, setSlug] = React.useState('');
  const [title_ru, setTitleRu] = React.useState('');
  const [title_en, setTitleEn] = React.useState('');
  const [title_zh, setTitleZh] = React.useState('');
  const [desc_ru, setDescRu]   = React.useState('');
  const [desc_en, setDescEn]   = React.useState('');
  const [desc_zh, setDescZh]   = React.useState('');
  const [sort_order, setSort]  = React.useState(0);
  const [is_active, setActive] = React.useState(true);

  // регион (обязателен)
  const [regions, setRegions] = React.useState([]);
  const [region_id, setRegionId] = React.useState('');

  // обложка
  const [images, setImages] = React.useState([]); // [{id,url,thumbnail_url,is_main}]
  const mainImage = React.useMemo(()=>{
    if (!images?.length) return null;
    return images.find(x=>x.is_main) || images[0];
  }, [images]);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const onPickCover = (list)=>{
    const file = list?.[0]; if (!file) return;
    const next = [{ id: file.id, url: file.url, thumbnail_url: file.thumbnail_url || file.url, is_main: true }];
    setImages(next);
    setPickerOpen(false);
  };

  // текущие элементы подборки
  const [items, setItems] = React.useState([]); // [{id, sort_order, property:{...}}]
  const [loadingItems, setLoadingItems] = React.useState(false);
  const loadItems = React.useCallback(async ()=>{
    if (!isEdit) return;
    setLoadingItems(true);
    try{
      const j = await AdminAPI.listCollectionItems(id);
      const arr = j.items || [];
      arr.sort((a,b)=> (a.sort_order ?? 0) - (b.sort_order ?? 0));
      setItems(arr);
    }catch(e){ console.error(e); }
    finally{ setLoadingItems(false); }
  }, [id, isEdit]);

  // загрузка регионов и коллекции
  React.useEffect(()=>{
    let stop = false;
    (async ()=>{
      try{ const r = await PublicAPI.regions({ locale:'ru' }); if(!stop) setRegions(r?.regions || []);}catch{ if(!stop) setRegions([]); }
      if (!isEdit) return;
      try{
        const j = await AdminAPI.getCollection(id);
        const it = j.item || j.collection || j;
        if (stop || !it) return;

        setSlug(it.slug || '');
        setTitleRu(it.title_ru || it.title || '');
        setTitleEn(it.title_en || it.title || '');
        setTitleZh(it.title_zh || '');
        setDescRu(it.description_ru || '');
        setDescEn(it.description_en || '');
        setDescZh(it.description_zh || '');
        setSort(it.sort_order || 0);
        setActive(!!it.is_active);
        setRegionId(it.region_id || '');

        if (it.featured_media?.id && it.featured_media?.url){
          setImages([{ id: it.featured_media.id, url: normalizeMediaUrl(it.featured_media.url), thumbnail_url: normalizeMediaUrl(it.featured_media.thumbnail_url || it.featured_media.url), is_main: true }]);
        } else if (it.cover){
          setImages([{ id: undefined, url: normalizeMediaUrl(it.cover), thumbnail_url: normalizeMediaUrl(it.cover_thumb || it.cover), is_main: true }]);
        } else {
          setImages([]);
        }
      }catch(e){
        console.error(e);
        setErr(e.message || 'Не удалось загрузить подборку');
      }
    })();
    return ()=>{ stop = true; };
  }, [isEdit, id]);

  React.useEffect(()=>{ loadItems(); }, [loadItems]);

  // --- поиск объектов для добавления (доступен и до сохранения) ---
  const [q, setQ] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [found, setFound] = React.useState([]); // список объектов
  const [selected, setSelected] = React.useState({}); // {propId:true}

  const selectedIds = React.useMemo(
    ()=> Object.keys(selected).filter(k => selected[k]).map(Number),
    [selected]
  );

  // id объектов, уже находящихся в подборке (чтобы подсветить/заблокировать повтор)
  const alreadyIn = React.useMemo(() => {
    const ids = new Set();
    for (const it of items || []) {
      if (it?.property?.id) ids.add(it.property.id);
    }
    return ids;
  }, [items]);

  const search = React.useCallback(async ()=>{
    setSearching(true);
    try{
      // при желании можно фильтровать активные: { is_active: true }
      const data = await AdminAPI.listProperties({ q, page_size: 50, region_id: region_id || undefined });
      const arr = data?.items || data?.properties || data || [];
      setFound(arr);
    }catch(e){
      console.error(e);
      alert('Не удалось выполнить поиск');
    }finally{
      setSearching(false);
    }
  }, [q, region_id]);

  // автозапрос при открытии страницы
  React.useEffect(() => { search(); }, [search]);

  const toggleSel = (pid, v)=> setSelected(s => ({...s, [pid]: v ?? !s[pid]}));

  // submit
  const saveCollection = async (opts = { addSelectedAfterCreate: false })=>{
    setErr(''); setOk(''); setSaving(true);
    try{
      if (!region_id) throw new Error('Укажите регион подборки');
      if (!title_ru && !title_en) throw new Error('Укажите заголовок RU или EN');

      const payload = {
        slug: (isEdit ? undefined : (slug || undefined)),
        title_ru: title_ru || undefined,
        title_en: title_en || undefined,
        title_zh: title_zh || undefined,
        description_ru: desc_ru || undefined,
        description_en: desc_en || undefined,
        description_zh: desc_zh || undefined,
        sort_order: Number(sort_order) || 0,
        is_active: !!is_active,
        featured_media_id: mainImage?.id || undefined,
        featured_image: (!mainImage?.id && mainImage?.url) ? mainImage.url : undefined,
        region_id: Number(region_id),
      };

      if (isEdit) {
        await AdminAPI.updateCollection(id, payload);
        setOk('Сохранено');
      } else {
        const res = await AdminAPI.createCollection(payload);
        const newId = res?.item?.id || res?.id || res?.collection?.id;
        if (!newId) throw new Error('Коллекция создана, но id не получен');
        setOk('Создано');

        // Если заранее отметили объекты — добавим их сразу
        if (opts.addSelectedAfterCreate && selectedIds.length){
          await Promise.all(selectedIds.map((pid, idx)=>
            AdminAPI.addCollectionItem(newId, { property_id: pid, sort_order: idx })
          ));
          setOk(`Создано и добавлено объектов: ${selectedIds.length}`);
          setSelected({});
        }
        nav(`/admin/collections/${newId}`);
        return; // дальше не продолжаем
      }
    }catch(e){
      setErr(e?.payload?.details ? JSON.stringify(e.payload.details) : (e.message || 'Ошибка сохранения'));
    }finally{ setSaving(false); }
  };

  const addSelected = async ()=>{
    if (!selectedIds.length) { alert('Выберите объекты'); return; }
    if (!isEdit){ alert('Сначала сохраните подборку, либо нажмите «Сохранить и добавить выбранные»'); return; }

    try{
      await Promise.all(selectedIds.map((pid, idx)=> AdminAPI.addCollectionItem(id, {
        property_id: pid, sort_order: (items.length + idx)
      })));
      setSelected({});
      await loadItems();
      setOk(`Добавлено: ${selectedIds.length}`);
    }catch(e){
      console.error(e);
      alert(e?.payload?.error || e?.message || 'Ошибка добавления');
    }
  };

  const addOne = async (pid)=>{
    if (!isEdit){ alert('Сначала сохраните подборку'); return; }
    try{
      await AdminAPI.addCollectionItem(id, { property_id: pid, sort_order: items.length });
      await loadItems();
    }catch(e){
      console.error(e);
      alert(e?.payload?.error || e?.message || 'Ошибка добавления');
    }
  };

  const delItem = async (itemId)=>{
    if(!window.confirm('Удалить объект из подборки?')) return;
    try{
      await AdminAPI.deleteCollectionItem(id, itemId);
      await loadItems();
    }catch(e){
      console.error(e);
      alert(e?.payload?.details || e?.message || 'Ошибка удаления');
    }
  };

  // ---- DnD сортировка ----
  const dragFrom = React.useRef(null);
  const onDragStart = (idx)=> (e)=> { dragFrom.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (idx)=> (e)=> { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (idx)=> async (e)=> {
    e.preventDefault();
    const from = dragFrom.current;
    if (from===null || from===undefined || from===idx) return;
    const next = [...items];
    const [moved] = next.splice(from,1);
    next.splice(idx, 0, moved);
    const withOrders = next.map((it, i) => ({ ...it, sort_order: i }));
    setItems(withOrders);
    dragFrom.current = null;

    try{
      await Promise.all(withOrders.map(it => (
        AdminAPI.updateCollectionItem(id, it.id, { sort_order: it.sort_order })
      )));
      setOk('Порядок сохранён');
    }catch(e){
      console.error(e);
      setErr('Не удалось сохранить порядок');
      await loadItems();
    }
  };

  return (
    <Wrap>
      <Card>
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <h3 style={{margin:0}}>{isEdit ? 'Редактировать подборку' : 'Новая подборка'}</h3>
          <div style={{flex:1}} />
          <Link to="/admin/collections" className="btn">← К списку</Link>
        </div>
      </Card>

      <Card>
        {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
        {ok &&  <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <Grid2>
          <Field>Регион
            <select value={region_id} onChange={e=>setRegionId(e.target.value)} required>
              <option value="">— выберите —</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.name_ru || r.name_en || r.name || r.slug}</option>
              ))}
            </select>
          </Field>
          <Field>Slug (генерируется сервером)
            <input value={slug} onChange={e=>setSlug(e.target.value)} placeholder="Будет заполнен после сохранения" disabled={!isEdit} />
          </Field>
          <Field>Сортировка
            <input type="number" value={sort_order} onChange={e=>setSort(e.target.value)} />
          </Field>
        </Grid2>

        <Grid2>
          <Field>Заголовок RU
            <input value={title_ru} onChange={e=>setTitleRu(e.target.value)} placeholder="Например: Лучшее в Dubai Marina" />
          </Field>
          <Field>Заголовок EN
            <input value={title_en} onChange={e=>setTitleEn(e.target.value)} placeholder="E.g. Best of Dubai Marina" />
          </Field>
        </Grid2>

        <Field>Заголовок ZH (中文)
          <input value={title_zh} onChange={e=>setTitleZh(e.target.value)} placeholder="例如：迪拜码头最佳选择" />
        </Field>

        <Grid2>
          <Field>Описание RU
            <textarea value={desc_ru} onChange={e=>setDescRu(e.target.value)} placeholder="Короткое описание (RU)" />
          </Field>
          <Field>Описание EN
            <textarea value={desc_en} onChange={e=>setDescEn(e.target.value)} placeholder="Short description (EN)" />
          </Field>
        </Grid2>

        <Field>Описание ZH (中文)
          <textarea value={desc_zh} onChange={e=>setDescZh(e.target.value)} placeholder="简短描述（中文）" />
        </Field>

        <Field>Активна
          <ToggleYesNo value={is_active} onChange={setActive} />
        </Field>
      </Card>

      {/* Обложка через медиатеку: используем «главную» как cover */}
      <Card>
        <h3 style={{marginTop:0}}>Обложка подборки</h3>
        <div style={{opacity:.75, marginBottom:8, fontSize:14}}>
          Выберите одно изображение. «Главная» считается обложкой. Если выбрано несколько — используем то, что помечено «Главная».
        </div>
        <PropertyImagesPicker
          value={images}
          onChange={setImages}
          autoDeleteUploads={false}
        />
        <div style={{marginTop:8}}>
          <button type="button" className="btn" onClick={()=>setPickerOpen(true)}>Выбрать из медиатеки</button>
        </div>
      </Card>

      <Card>
        <Actions>
          <Btn data-primary="true" onClick={()=>setConfirmOpen(true)} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Btn>
          {!isEdit && (
            <Btn
              data-primary="true"
              onClick={()=>setConfirmOpen(true)}
              disabled={saving || !selectedIds.length}
              title={selectedIds.length ? '' : 'Сначала отметьте объекты ниже'}
            >
              {saving ? 'Сохранение…' : `Сохранить и добавить выбранные (${selectedIds.length})`}
            </Btn>
          )}
          <Link to="/admin/collections" className="btn">Отмена</Link>
          <div style={{flex:1}} />
          {isEdit && <Pills><Pill>id: <b>{id}</b></Pill></Pills>}
        </Actions>
      </Card>

      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position:'fixed', inset:0, zIndex:3500, background:'rgba(0,0,0,.35)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={()=>setConfirmOpen(false)}
        >
          <div
            style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16, boxShadow:'var(--shadow)', padding:18, minWidth:320 }}
            onClick={(e)=>e.stopPropagation()}
          >
            <div style={{fontWeight:800, marginBottom:8}}>{isEdit ? 'Сохранить изменения в подборке?' : 'Создать подборку?'}</div>
            <div style={{opacity:.8, fontSize:13, marginBottom:12}}>Подтвердите действие или отмените.</div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <Btn onClick={()=>setConfirmOpen(false)}>Отмена</Btn>
              <Btn data-primary="true" onClick={()=>{ setConfirmOpen(false); saveCollection({ addSelectedAfterCreate: !isEdit && selectedIds.length>0 }); }}>Да, {isEdit ? 'сохранить' : 'создать'}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Поиск и добавление объектов — видно всегда */}
      <Card>
        <h3 style={{marginTop:0}}>Добавить объекты</h3>
        {!isEdit && (
          <div style={{opacity:.75, marginBottom:8, fontSize:14}}>
            Вы можете отметить объекты сразу. Они будут добавлены автоматически после нажатия
            «Сохранить и добавить выбранные».
          </div>
        )}

        <SearchBar>
          <input
            placeholder="Поиск по названию/ID…"
            value={q}
            onChange={e=>setQ(e.target.value)}
            onKeyDown={(e)=>{ if (e.key === 'Enter') search(); }}
          />
          <Btn onClick={search} disabled={searching}>{searching ? 'Поиск…' : 'Найти'}</Btn>
          <Btn onClick={addSelected} disabled={!isEdit || !selectedIds.length} title={isEdit ? '' : 'Сначала сохраните подборку'}>
            + Добавить выбранные {selectedIds.length ? `(${selectedIds.length})` : ''}
          </Btn>
        </SearchBar>

        {!!found.length && (
          <Table>
            <thead>
              <tr>
                <th style={{width:36}}></th>
                <th>Объект</th>
                <th>Адрес / Регион</th>
                <th>Площадь</th>
                <th>Спальни</th>
                <th>Цена</th>
                <th style={{width:120}}></th>
              </tr>
            </thead>
            <tbody>
              {found.map(p=>{
                const inCollection = alreadyIn.has(p.id);
                return (
                <tr key={p.id} style={inCollection ? {opacity:.55} : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      onChange={e=>toggleSel(p.id, e.target.checked)}
                      disabled={inCollection}
                      title={inCollection ? 'Уже в подборке' : ''}
                    />
                  </td>
                  <td style={{display:'flex', alignItems:'center', gap:8}}>
                    {getThumb(p) ? <img src={getThumb(p)} alt="" /> : null}
                    <div>
                      <div style={{fontWeight:700}}>{p.title_ru || p.title_en || p.title || `#${p.id}`}</div>
                      <div style={{opacity:.7, fontSize:12}}>#{p.id} • {p.property_type?.name_ru || p.property_type?.name_en || p.property_type?.name || '—'}</div>
                    </div>
                  </td>
                  <td>{[p.region?.name, p.district?.name].filter(Boolean).join(' — ')}</td>
                  <td>{p.area_total ? `${p.area_total} м²` : '—'}</td>
                  <td>{(p.bedrooms ?? '') === '' ? '—' : p.bedrooms}</td>
                  <td>{p.price ? `${p.price} ${p.currency?.code || ''}` : '—'}</td>
                  <td>
                    {inCollection ? (
                      <span style={{fontSize:12, opacity:.8}}>В подборке</span>
                    ) : (
                      <Btn onClick={()=>addOne(p.id)} disabled={!isEdit} title={isEdit ? '' : 'Сначала сохраните подборку'}>
                        + Добавить
                      </Btn>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </Table>
        )}
        {!found.length && searching && <div style={{opacity:.7, marginTop:8}}>Поиск…</div>}
      </Card>

      {/* Состав подборки с drag-and-drop сортировкой */}
      {isEdit && (
        <Card>
          <h3 style={{marginTop:0}}>Состав подборки</h3>
          {loadingItems ? (
            <div style={{opacity:.7}}>Загрузка…</div>
          ) : !items.length ? (
            <div style={{opacity:.6}}>Пока пусто. Добавьте объекты выше.</div>
          ) : (
            <ItemsGrid>
              {items.map((it, idx)=>(
                <Row
                  key={it.id}
                  draggable
                  onDragStart={onDragStart(idx)}
                  onDragOver={onDragOver(idx)}
                  onDrop={onDrop(idx)}
                >
                  {getThumb(it.property) ? <Thumb src={getThumb(it.property)} alt="" /> : <div />}
                  <div>
                    <div style={{fontWeight:700}}>{it.property?.title_ru || it.property?.title_en || it.property?.title || `#${it.property?.id}`}</div>
                    <div style={{opacity:.7, fontSize:12}}>
                      #{it.property?.id} • {[it.property?.region?.name, it.property?.district?.name].filter(Boolean).join(' — ')}
                    </div>
                  </div>
                  <div style={{opacity:.8, fontSize:14}}>
                    {it.property?.area_total ? `${it.property.area_total} м²` : '—'}
                  </div>
                  <div style={{opacity:.8, fontSize:14}}>
                    {it.property?.price ? `${it.property.price} ${it.property?.currency?.code || ''}` : '—'}
                  </div>
                  <div style={{display:'flex', gap:8, alignItems:'center', justifyContent:'flex-end'}}>
                    <DragHandle title="Перетащите для сортировки">↕</DragHandle>
                    <Btn onClick={()=>delItem(it.id)}>Удалить</Btn>
                  </div>
                </Row>
              ))}
            </ItemsGrid>
          )}
          <Hr />
          <div style={{opacity:.7, fontSize:13}}>
            Подсказка: перетаскивайте строки (↕), порядок сохраняется автоматически.
          </div>
        </Card>
      )}

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={onPickCover} multi={false} />
    </Wrap>
  );
}
